import { ActivePromoBenefit, Firm, FirmSubscription } from '@/types';
import { logger } from '@/utils/logger';

export type PaymentMethodStatus = 'valid' | 'missing' | 'failed' | 'inactive' | 'past_due' | 'unpaid';

/**
 * Whether the firm has at least one `free_cases` promo benefit with remaining
 * capacity that hasn't expired. The backend already treats this as a valid
 * client-creation gate (see functions/src/modules/billing/billing.service.ts);
 * this helper brings the frontend in line.
 */
export function hasUsableFreeCasesPromo(firm: Firm | null): boolean {
  return hasUsableFreeCasesPromoBenefits(firm?.activePromoBenefits);
}

export function hasUsableFreeCasesPromoBenefits(
  benefits: ActivePromoBenefit[] | undefined,
): boolean {
  if (!benefits?.length) return false;
  const now = new Date();
  return benefits.some((b) => {
    if (b.type !== 'free_cases') return false;
    if (b.expiresAt) {
      const raw = b.expiresAt as { toDate?: () => Date };
      const exp = typeof raw.toDate === 'function'
        ? raw.toDate()
        : new Date(b.expiresAt as unknown as string | number | Date);
      if (exp < now) return false;
    }
    return (b.freeCasesRemaining ?? 0) > 0;
  });
}

/**
 * Check payment method status for a firm
 * Returns the status and reason if invalid
 */
export function checkPaymentMethodStatus(firm: Firm | null): {
  status: PaymentMethodStatus;
  reason?: 'missing' | 'failed' | 'inactive' | 'past_due' | 'unpaid';
} {
  if (!firm || !firm.subscription) {
    return { status: 'missing', reason: 'missing' };
  }

  const subscription: FirmSubscription = firm.subscription;

  // Check subscription status
  if (subscription.status === 'past_due') {
    return { status: 'past_due', reason: 'past_due' };
  }

  if (subscription.status === 'unpaid') {
    return { status: 'unpaid', reason: 'unpaid' };
  }

  // Check if payment method exists
  // For trial accounts, stripePaymentMethodId should exist
  // For active accounts, stripeSubscriptionId or stripeCustomerId should exist
  // We also accept stripeCustomerId alone as valid (customer exists in Stripe)
  const hasPaymentMethod =
    subscription.stripePaymentMethodId ||
    subscription.stripeSubscriptionId ||
    subscription.stripeCustomerId ||
    ((subscription as unknown as { paymentMethods?: unknown[] }).paymentMethods?.length ?? 0) > 0;

  if (!hasPaymentMethod) {
    logger.debug('checkPaymentMethodStatus: No payment method found', {
      status: subscription.status,
      stripeCustomerId: !!subscription.stripeCustomerId,
      stripeSubscriptionId: !!subscription.stripeSubscriptionId,
      stripePaymentMethodId: !!subscription.stripePaymentMethodId,
    });
    return { status: 'missing', reason: 'missing' };
  }

  // If subscription status is 'active' and payment method exists, it's valid
  // 'paused' is also valid (used for $0 invoices/free tier)
  // 'trialing' is valid (Stripe trial)
  // 'trial' is valid (Legacy app status)
  // 'incomplete' is valid (Initial payment processing)
  // 'inactive' is valid (If payment method exists, allow access/reactivation)
  const validStatuses = ['active', 'paused', 'trialing', 'trial', 'incomplete', 'inactive'];
  if (validStatuses.includes(subscription.status)) {
    return { status: 'valid' };
  }

  // Default to missing if we can't determine
  logger.warn('checkPaymentMethodStatus: Invalid status', {
    status: subscription.status,
    hasPaymentMethod,
    firmId: firm.firmId
  });
  logger.debug('checkPaymentMethodStatus: Unknown status, defaulting to missing', { subscriptionStatus: subscription.status });
  return { status: 'missing', reason: 'missing' };
}

/**
 * Check if payment method is valid (allows access to app features)
 */
export function isPaymentMethodValid(firm: Firm | null): boolean {
  const { status } = checkPaymentMethodStatus(firm);
  return status === 'valid';
}

/**
 * Get routes that are allowed even when payment method is invalid
 */
export function getAllowedRoutes(): string[] {
  return ['/billing', '/settings', '/firm-settings'];
}

/**
 * Check if a route is allowed when payment method is invalid
 */
export function isRouteAllowed(route: string): boolean {
  const allowedRoutes = getAllowedRoutes();
  return allowedRoutes.some(allowed => route.startsWith(allowed));
}

/**
 * Whether a payment-method failure should hard-gate the app (force redirect to /billing).
 * `past_due` and `unpaid` mean the firm had a working subscription that broke — they
 * need urgent attention. `missing` means a brand-new firm that has never added a card —
 * they get a soft gate (banner only, free to browse) to allow product exploration.
 */
export function shouldHardGate(reason?: 'missing' | 'failed' | 'inactive' | 'past_due' | 'unpaid'): boolean {
  return reason === 'past_due' || reason === 'unpaid';
}

/**
 * Canonical predicate for "is this firm allowed to create clients?". Mirrors the
 * denormalized `firm.hasUsableClientCreationCapacity` boolean maintained by the
 * onFirmWriteCapacity Firestore trigger and read by the firestore.rules
 * `firmCanCreateClient` helper. Frontend and backend MUST stay in lockstep —
 * any logic change here needs the matching change in
 * functions/src/shared/clientCreationCapacity.ts (and a re-run of the backfill
 * script).
 *
 * Inputs:
 *   - `valid` payment status (any of stripeCustomerId / subscriptionId /
 *     paymentMethodId / non-empty paymentMethods[]) → true
 *   - hard-gate (`past_due` / `unpaid`) → false (promos do not bypass)
 *   - usable `free_cases` promo (not expired, freeCasesRemaining > 0) → true
 */
export function hasUsableClientCreationCapacity(firm: Firm | null): boolean {
  const { status, reason } = checkPaymentMethodStatus(firm);
  if (status === 'valid') return true;
  if (shouldHardGate(reason)) return false;
  return hasUsableFreeCasesPromo(firm);
}

/**
 * Whether the firm is allowed to create clients. Thin alias kept for call-site
 * readability; semantically identical to `hasUsableClientCreationCapacity`.
 */
export function canCreateClient(firm: Firm | null): boolean {
  return hasUsableClientCreationCapacity(firm);
}

/**
 * How a firm is "billing ready" for onboarding copy (#1315). This is a
 * classification over `hasUsableClientCreationCapacity` — `kind !== 'none'`
 * is exactly equivalent to it, so onboarding never disagrees with the
 * client-creation gate. It only adds *why*: a card, or promo coverage.
 */
export type BillingReadiness =
  | { kind: 'card' }
  | { kind: 'promo' }
  | { kind: 'none'; reason: 'missing' | 'failed' | 'inactive' | 'past_due' | 'unpaid' };

export function getBillingReadiness(firm: Firm | null): BillingReadiness {
  const { status, reason } = checkPaymentMethodStatus(firm);
  if (status === 'valid') return { kind: 'card' };
  if (shouldHardGate(reason)) return { kind: 'none', reason: reason ?? 'missing' };
  if (hasUsableFreeCasesPromo(firm)) return { kind: 'promo' };
  return { kind: 'none', reason: reason ?? 'missing' };
}



